import { useQuery } from '@tanstack/react-query';
import type { BrandWorkspaceFlagView } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { brandKeys } from './queries';

/**
 * The install's Brand Workspace v2 flag (final-design §2.15), read once per session (`['brand-flag']`,
 * never stale). The flag changes only through the migration's unscoped apply or rollback, and a page
 * open across that change keeps what it read: its old saves are refused with a sentence (risk 13).
 */

/**
 * Reads the flag. A 404 means an API from before Release 3: the SPA and the API ship in one image, but
 * during a revision switch a new SPA can meet the old API, which has no such route, and there the
 * flag is off. Any other failure is thrown: the caller shows an error and never guesses, because the old
 * pages would write brand text that is read-only while the flag is on.
 */
export async function fetchBrandWorkspaceFlag(signal?: AbortSignal): Promise<BrandWorkspaceFlagView> {
  try {
    return await api.getBrandWorkspaceFlag(signal);
  } catch (err) {
    if (err instanceof ApiRequestError && err.status === 404) return { enabled: false };
    throw err;
  }
}

export interface BrandWorkspaceV2State {
  /** true: v2 pages; false: today's pages; undefined: not known yet (loading, or the read failed). */
  enabled: boolean | undefined;
  /** Not known yet and no failure to show: the first read, or a retry, is running. */
  isLoading: boolean;
  /** The read failed (not a 404) and nothing is running: show an error with Retry. */
  isError: boolean;
  /** Reads again (Retry). */
  refetch: () => void;
}

/**
 * Whether Brand Workspace v2 is known to be on, from the read WorkspaceGate makes for the whole session:
 * this only looks at it and never reads it itself (`enabled: false`), so it adds no request. Until the
 * flag is known, and while it is off, false: for what only names v2 features, today's page is the safe
 * reading.
 */
export function useBrandWorkspaceV2Known(): boolean {
  const query = useQuery({
    queryKey: brandKeys.flag,
    queryFn: ({ signal }) => fetchBrandWorkspaceFlag(signal),
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });
  return query.data?.enabled === true;
}

/** Whether Brand Workspace v2 is on for this install (web.md §2.1). */
export function useBrandWorkspaceV2(): BrandWorkspaceV2State {
  const query = useQuery({
    queryKey: brandKeys.flag,
    queryFn: ({ signal }) => fetchBrandWorkspaceFlag(signal),
    staleTime: Infinity,
    retry: false,
  });
  const enabled = query.data?.enabled;
  const isError = enabled === undefined && query.isError && !query.isFetching;
  return {
    enabled,
    isLoading: enabled === undefined && !isError,
    isError,
    refetch: () => void query.refetch(),
  };
}
